import { useEffect, useState } from "react";
import { Save, Loader2, ChevronUp, ChevronDown, LayoutGrid } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import ImageUpload from "../../components/ImageUpload";

const DEFAULT_SECTION_ORDER = ["hero", "marquee", "services", "why", "process", "band", "cta"];
const SECTION_NAMES = {
  hero: "Hero - big banner with crest",
  marquee: "Scrolling marquee strip",
  services: "Services - what we do boxes",
  why: "Why Dogs - the nose knows",
  process: "Process - from call to sweep report",
  band: "Quote band - one dog one sweep",
  cta: "Call to action - phone and message",
};

const PAGE_TABS = [
  ["home", "Home"],
  ["dogs", "Meet Our Dogs"],
  ["sale", "Dogs for Sale"],
  ["contact", "Contact"],
  ["about", "About Us"],
  ["blog", "Blog"],
  ["frame", "Logo, Menu & Footer"],
];
const TAB_NAMES = Object.fromEntries(PAGE_TABS);
const TAB_GROUP = { dogs: "dogs", sale: "sale", contact: "contact", about: "about_page", blog: "blog" };
const PAGE_FIELDS = {
  dogs: [["eyebrow", "Small label"], ["title", "Title (white words)"], ["title_accent", "Title (crimson words)"], ["intro", "Intro paragraph"], ["empty", "Empty-state message"]],
  sale: [["eyebrow", "Small label"], ["title", "Title (white words)"], ["title_accent", "Title (crimson words)"], ["intro", "Intro paragraph"], ["empty", "No-dogs message"], ["card_button", "Card button"], ["rehome_title", "Rehoming title"], ["rehome_accent", "Rehoming title (crimson)"], ["rehome_button", "Rehoming button"]],
  contact: [["eyebrow", "Small label"], ["title", "Title (white words)"], ["title_accent", "Title (crimson words)"], ["intro", "Intro paragraph"], ["f_name", "Label - Full Name"], ["f_email", "Label - Email"], ["f_phone", "Label - Phone"], ["f_date", "Label - Date Needed"], ["f_time", "Label - Time Needed"], ["f_location", "Label - Location"], ["f_service", "Label - Service Required"], ["f_service_ph", "Service dropdown placeholder"], ["f_message", "Label - Your Message"], ["urgent_label", "Urgent switch label"], ["submit", "Submit button"], ["sending", "Sending text"]],
  about_page: [["eyebrow", "Small label at top"], ["values_eyebrow", "Values label"], ["values_title", "Values title"], ["acc_eyebrow", "Accreditations label"], ["acc_title", "Accreditations title"]],
  blog: [["eyebrow", "Small label"], ["title", "Title (white words)"], ["title_accent", "Title (crimson words)"], ["intro", "Intro paragraph"], ["empty", "No-posts message"]],
};


const blank = {
  section_order: DEFAULT_SECTION_ORDER,
  hero: { eyebrow: "", line1: "", line2: "", subtitle: "" },
  contact: { phone: "", email: "", coverage: "", hours: "" },
  about: { title: "", story: [], image: "" },
  marquee: { items: ["NASDU Accredited", "BS 8517 Compliant", "SIA Licensed Handlers", "Explosives Detection", "Narcotics Detection", "Knife & Weapon Detection", "24/7 Deployment"] },
  services: {
    title: "One team. Every threat covered.",
    cards: [
      { title: "Explosives & Narcotics Sweeps", desc: "Discreet, methodical sweeps of vehicles, buildings and grounds before your people arrive. Every find documented, every sweep reported." },
      { title: "Knife & Weapon Detection", desc: "Passive screening teams that keep blades out of your venue without a single pat-down." },
      { title: "School & Venue Inspections", desc: "Friendly, non-intrusive searches designed for educational environments and safeguarding policies." },
      { title: "Corporate & Event Protection", desc: "A visible deterrent with invisible operations. K9 teams for offices, festivals, stadiums and private events." },
    ],
  },
  why: {
    title: "The nose knows. Nothing hides from it.",
    intro: "Technology helps, but it cannot rival a trained detection dog. Our teams find what scanners miss - faster, calmer and with a lot less fuss for your guests.",
    rows: [
      { title: "Speed", desc: "A single detection dog can clear an area in a fraction of the time of a manual search team." },
      { title: "Accuracy", desc: "A dog's nose is thousands of times more sensitive than any handheld device - nothing gets missed." },
      { title: "Non-Intrusive", desc: "No queues, no physical contact, no disruption. Your guests barely notice us working." },
      { title: "Compliance", desc: "Fully accredited teams working to British Standards, with a formal report after every deployment." },
    ],
  },
  process: {
    title: "From call to sweep report",
    steps: [
      { title: "Rapid Assessment", desc: "Tell us your venue, dates and risk profile. We advise within the hour." },
      { title: "Handler Dispatch", desc: "The nearest accredited K9 team mobilises with full kit and documentation." },
      { title: "Methodical Sweep", desc: "A systematic search of entry points, buildings and vehicles, coordinated with your security." },
      { title: "Formal Sweep Report", desc: "A written record of the sweep for your files, insurers and licensing." },
    ],
  },
  band: {
    quote: '"One dog, one sweep, complete confidence."',
    image: "",
  },
  cta: {
    eyebrow: "Ready When You Are",
    title: "Secure your venue this week",
    text: "Request a sweep, book one of our dogs for your event, or ask us anything - we'll come back with a plan, a team and a fixed price, usually within the hour.",
  },
  chrome: {
    nav_request_button: "Request a Sweep",
    nav_labels: {},
    hero_primary: "Request a Free Consultation",
    hero_secondary: "Meet the Dogs",
    cta_message_label: "Send Us a Message",
  },
  pages: {
    logo_title: "", logo_subtitle: "", footer_text: "", home_badges: "",
    dogs: {}, sale: {}, contact: {}, about_page: {}, blog: {},
  },
};

export default function ContentTab() {
  const [content, setContent] = useState(null);
  const [busy, setBusy] = useState(false);
  const [activePage, setActivePage] = useState("home");

  useEffect(() => {
    api.get("/content").then((r) => setContent({ ...blank, ...r.data })).catch(() => setContent(blank));
  }, []);

  const setField = (section, key, value) =>
    setContent((c) => ({ ...c, [section]: { ...c[section], [key]: value } }));

  const setListField = (section, key, idx, field, value) =>
    setContent((c) => ({
      ...c,
      [section]: {
        ...c[section],
        [key]: (c[section][key] || []).map((row, i) => (i === idx ? { ...row, [field]: value } : row)),
      },
    }));

  const setSectionOrder = (order) => setContent((c) => ({ ...c, section_order: order }));

  const moveSection = (i, dir) => {
    const order = [...(content.section_order || DEFAULT_SECTION_ORDER)];
    const j = i + dir;
    if (j < 0 || j >= order.length) return;
    [order[i], order[j]] = [order[j], order[i]];
    setSectionOrder(order);
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/content", { content });
      toast.success("Site content saved", { description: "Changes are live on the website immediately." });
    } catch (e) {
      toast.error("Could not save content", { description: formatApiError(e) });
    } finally {
      setBusy(false);
    }
  };

  if (!content) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-brand-crimson" data-testid="content-loading-spinner" />
      </div>
    );
  }

  const pg = content.pages || {};

  return (
    <div className="max-w-2xl space-y-8" data-testid="content-tab">
      <div className="flex flex-wrap gap-2" data-testid="content-page-picker">
        {PAGE_TABS.map(([id, name]) => (
          <button
            key={id}
            data-testid={`pages-tab-${id}`}
            onClick={() => setActivePage(id)}
            className={`px-4 py-2 rounded-full border text-[11px] font-eyebrow uppercase tracking-[0.15em] font-bold transition-colors ${activePage === id ? "bg-brand-crimson border-brand-crimson text-white" : "border-brand-line text-white/60 hover:text-white hover:border-white/40"}`}
          >
            {name}
          </button>
        ))}
      </div>
      <div data-testid="content-page-heading">
        <p className="font-eyebrow text-[10px] uppercase tracking-[0.25em] text-white/40 font-bold mb-0.5">Now editing</p>
        <h2 className="text-2xl font-bold text-white" data-testid="content-page-name">{TAB_NAMES[activePage] || ""}</h2>
      </div>

      {activePage === "home" && (
      <section className="rounded-lg border border-brand-line bg-brand-surface p-6" data-testid="content-layout-section">
        <p className="font-eyebrow text-[10px] uppercase tracking-[0.25em] text-brand-crimson font-bold mb-1">Homepage layout</p>
        <p className="text-xs text-white/50 mb-4">Move the homepage sections up and down - the website follows this order top to bottom. Every section's text and images are edited in the panels below.</p>
        <div className="space-y-2">
          {(content.section_order || DEFAULT_SECTION_ORDER).map((id, i, arr) => (
            <div key={id} className="flex items-center gap-3 rounded-md border border-brand-line bg-brand-obsidian px-4 py-2.5" data-testid={`layout-row-${id}`}>
              <LayoutGrid className="h-4 w-4 text-white/30 shrink-0" />
              <span className="flex-1 text-sm text-white">{SECTION_NAMES[id] || id}</span>
              <button onClick={() => moveSection(i, -1)} disabled={i === 0} data-testid={`layout-up-${id}`} className="p-1.5 rounded border border-brand-line text-white/50 hover:text-white transition-colors disabled:opacity-25" aria-label="Move section up">
                <ChevronUp className="h-4 w-4" />
              </button>
              <button onClick={() => moveSection(i, 1)} disabled={i === arr.length - 1} data-testid={`layout-down-${id}`} className="p-1.5 rounded border border-brand-line text-white/50 hover:text-white transition-colors disabled:opacity-25" aria-label="Move section down">
                <ChevronDown className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-white/40 mt-3">Press Save to apply the new order to the website.</p>
      </section>
      )}

      {activePage === "frame" && (
      <section className="rounded-lg border border-brand-line bg-brand-surface p-6" data-testid="content-buttons-section">
        <p className="font-eyebrow text-[10px] uppercase tracking-[0.25em] text-brand-crimson font-bold mb-1">Buttons &amp; navigation</p>
        <p className="text-xs text-white/50 mb-4">Every button label and menu name on the site - change the words and press Save.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-white/70 text-xs">Top navigation button (and mobile menu)</Label>
            <Input data-testid="chrome-nav-request" value={content.chrome?.nav_request_button || ""} onChange={(e) => setField("chrome", "nav_request_button", e.target.value)} className="bg-brand-obsidian border-brand-line" />
          </div>
          {[
            ["/", "Menu name - Home"],
            ["/meet-our-dogs", "Menu name - Meet Our Dogs"],
            ["/blog", "Menu name - Blog"],
            ["/about-us", "Menu name - About Us"],
            ["/contact-us", "Menu name - Contact Us"],
          ].map(([path, label]) => (
            <div key={path} className="space-y-1.5">
              <Label className="text-white/70 text-xs">{label}</Label>
              <Input
                data-testid={`chrome-nav-label-${path.slice(1) || "home"}`}
                value={(content.chrome?.nav_labels || {})[path] || ""}
                onChange={(e) => setField("chrome", "nav_labels", { ...(content.chrome?.nav_labels || {}), [path]: e.target.value })}
                className="bg-brand-obsidian border-brand-line"
              />
            </div>
          ))}
          <div className="space-y-1.5">
            <Label className="text-white/70 text-xs">Hero button 1 (crimson)</Label>
            <Input data-testid="chrome-hero-primary" value={content.chrome?.hero_primary || ""} onChange={(e) => setField("chrome", "hero_primary", e.target.value)} className="bg-brand-obsidian border-brand-line" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-white/70 text-xs">Hero button 2 (outline)</Label>
            <Input data-testid="chrome-hero-secondary" value={content.chrome?.hero_secondary || ""} onChange={(e) => setField("chrome", "hero_secondary", e.target.value)} className="bg-brand-obsidian border-brand-line" />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-white/70 text-xs">Homepage bottom button - message</Label>
            <Input data-testid="chrome-cta-message" value={content.chrome?.cta_message_label || ""} onChange={(e) => setField("chrome", "cta_message_label", e.target.value)} className="bg-brand-obsidian border-brand-line" />
          </div>
        </div>
        <p className="text-[11px] text-white/40 mt-3">Leave a menu name empty to use the default.</p>
      </section>
      )}

      {activePage === "frame" && (
      <section className="rounded-lg border border-brand-line bg-brand-surface p-6" data-testid="content-pages-frame-section">
        <p className="font-eyebrow text-[10px] uppercase tracking-[0.25em] text-brand-crimson font-bold mb-1">Logo &amp; footer</p>
        <p className="text-xs text-white/50 mb-4">The crest words and footer description, shown on every page.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-white/70 text-xs">Logo - first line</Label>
            <Input data-testid="pages-logo-title" value={pg.logo_title || ""} onChange={(e) => setField("pages", "logo_title", e.target.value)} className="bg-brand-obsidian border-brand-line" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-white/70 text-xs">Logo - second line (crimson)</Label>
            <Input data-testid="pages-logo-subtitle" value={pg.logo_subtitle || ""} onChange={(e) => setField("pages", "logo_subtitle", e.target.value)} className="bg-brand-obsidian border-brand-line" />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-white/70 text-xs">Footer description (under the crest)</Label>
            <Textarea data-testid="pages-footer-text" value={pg.footer_text || ""} onChange={(e) => setField("pages", "footer_text", e.target.value)} rows={2} className="bg-brand-obsidian border-brand-line" />
          </div>
        </div>
      </section>
      )}

      {activePage === "home" && (
      <section className="rounded-lg border border-brand-line bg-brand-surface p-6" data-testid="content-home-badges-section">
        <p className="font-eyebrow text-[10px] uppercase tracking-[0.25em] text-brand-crimson font-bold mb-1">Homepage trust badges</p>
        <p className="text-xs text-white/50 mb-4">The small badges under the hero buttons. Separate each badge with |</p>
        <Input data-testid="pages-home-badges" value={pg.home_badges || ""} onChange={(e) => setField("pages", "home_badges", e.target.value)} className="bg-brand-obsidian border-brand-line" />
      </section>
      )}

      {TAB_GROUP[activePage] && (
      <section className="rounded-lg border border-brand-line bg-brand-surface p-6" data-testid={`content-pages-section-${activePage}`}>
        <p className="font-eyebrow text-[10px] uppercase tracking-[0.25em] text-brand-crimson font-bold mb-1">{TAB_NAMES[activePage]} - every word</p>
        <p className="text-xs text-white/50 mb-4">Every label and line of wording on this page. Leave anything empty to use the default.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {PAGE_FIELDS[TAB_GROUP[activePage]].map(([key, lab]) => (
            <div key={key} className="space-y-1">
              <Label className="text-white/50 text-[11px]">{lab}</Label>
              <Input
                data-testid={`pages-${TAB_GROUP[activePage]}-${key}`}
                value={(pg[TAB_GROUP[activePage]] || {})[key] || ""}
                onChange={(e) => setField("pages", TAB_GROUP[activePage], { ...(pg[TAB_GROUP[activePage]] || {}), [key]: e.target.value })}
                className="bg-brand-surface border-brand-line text-sm"
              />
            </div>
          ))}
        </div>
        <p className="text-[11px] text-white/40 mt-4">Press Save to apply your changes to the website.</p>
      </section>
      )}

      {activePage === "home" && (
      <section className="rounded-lg border border-brand-line bg-brand-surface p-6" data-testid="content-hero-section">
        <p className="font-eyebrow text-[10px] uppercase tracking-[0.25em] text-brand-crimson font-bold mb-5">Homepage Hero</p>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-white/70 text-xs">Eyebrow Label</Label>
            <Input data-testid="content-hero-eyebrow" value={content.hero.eyebrow || ""} onChange={(e) => setField("hero", "eyebrow", e.target.value)} className="bg-brand-obsidian border-brand-line" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-white/70 text-xs">Headline Line 1</Label>
              <Input data-testid="content-hero-line1" value={content.hero.line1 || ""} onChange={(e) => setField("hero", "line1", e.target.value)} className="bg-brand-obsidian border-brand-line" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-white/70 text-xs">Headline Line 2</Label>
              <Input data-testid="content-hero-line2" value={content.hero.line2 || ""} onChange={(e) => setField("hero", "line2", e.target.value)} className="bg-brand-obsidian border-brand-line" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-white/70 text-xs">Subheadline</Label>
            <Textarea data-testid="content-hero-subtitle" value={content.hero.subtitle || ""} onChange={(e) => setField("hero", "subtitle", e.target.value)} rows={3} className="bg-brand-obsidian border-brand-line" />
          </div>
        </div>
      </section>
      )}

      {activePage === "contact" && (
      <section className="rounded-lg border border-brand-line bg-brand-surface p-6" data-testid="content-contact-section">
        <p className="font-eyebrow text-[10px] uppercase tracking-[0.25em] text-brand-crimson font-bold mb-5">Contact Details (shown site-wide)</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-white/70 text-xs">Phone</Label>
            <Input data-testid="content-contact-phone" value={content.contact.phone || ""} onChange={(e) => setField("contact", "phone", e.target.value)} className="bg-brand-obsidian border-brand-line" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-white/70 text-xs">Email</Label>
            <Input data-testid="content-contact-email" value={content.contact.email || ""} onChange={(e) => setField("contact", "email", e.target.value)} className="bg-brand-obsidian border-brand-line" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-white/70 text-xs">Website</Label>
            <Input data-testid="content-contact-website" value={content.contact.website || ""} onChange={(e) => setField("contact", "website", e.target.value)} className="bg-brand-obsidian border-brand-line" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-white/70 text-xs">Coverage</Label>
            <Input data-testid="content-contact-coverage" value={content.contact.coverage || ""} onChange={(e) => setField("contact", "coverage", e.target.value)} className="bg-brand-obsidian border-brand-line" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-white/70 text-xs">Hours / Availability</Label>
            <Input data-testid="content-contact-hours" value={content.contact.hours || ""} onChange={(e) => setField("contact", "hours", e.target.value)} className="bg-brand-obsidian border-brand-line" />
          </div>
        </div>
      </section>
      )}

      {activePage === "about" && (
      <section className="rounded-lg border border-brand-line bg-brand-surface p-6" data-testid="content-about-section">
        <p className="font-eyebrow text-[10px] uppercase tracking-[0.25em] text-brand-crimson font-bold mb-5">About Us Page</p>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-white/70 text-xs">Page Title</Label>
            <Input data-testid="content-about-title" value={content.about.title || ""} onChange={(e) => setField("about", "title", e.target.value)} className="bg-brand-obsidian border-brand-line" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-white/70 text-xs">Story (one paragraph per line, blank line between)</Label>
            <Textarea
              data-testid="content-about-story"
              value={(content.about.story || []).join("\n\n")}
              onChange={(e) => setField("about", "story", e.target.value.split("\n\n").filter(Boolean))}
              rows={8}
              className="bg-brand-obsidian border-brand-line"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-white/70 text-xs">About Page Image</Label>
            <ImageUpload value={content.about.image} onChange={(url) => setField("about", "image", url)} testid="content-about-image-upload" />
          </div>
        </div>
      </section>
      )}

      {activePage === "home" && (
      <>
      <section className="rounded-lg border border-brand-line bg-brand-surface p-6" data-testid="content-marquee-section">
        <p className="font-eyebrow text-[10px] uppercase tracking-[0.25em] text-brand-crimson font-bold mb-5">Scrolling Marquee (one phrase per line)</p>
        <Textarea
          data-testid="content-marquee-items"
          value={(content.marquee.items || []).join("\n")}
          onChange={(e) => setField("marquee", "items", e.target.value.split("\n").filter(Boolean))}
          rows={5}
          className="bg-brand-obsidian border-brand-line"
        />
      </section>

      <section className="rounded-lg border border-brand-line bg-brand-surface p-6" data-testid="content-services-section">
        <p className="font-eyebrow text-[10px] uppercase tracking-[0.25em] text-brand-crimson font-bold mb-5">Services Section</p>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-white/70 text-xs">Section Heading</Label>
            <Input data-testid="content-services-title" value={content.services.title || ""} onChange={(e) => setField("services", "title", e.target.value)} className="bg-brand-obsidian border-brand-line" />
          </div>
          {(content.services.cards || []).map((card, i) => (
            <div key={i} className="grid sm:grid-cols-[230px_1fr] gap-3" data-testid={`content-service-card-${i}`}>
              <Input value={card.title || ""} onChange={(e) => setListField("services", "cards", i, "title", e.target.value)} placeholder={`Box ${i + 1} title`} className="bg-brand-obsidian border-brand-line" />
              <Input value={card.desc || ""} onChange={(e) => setListField("services", "cards", i, "desc", e.target.value)} placeholder={`Box ${i + 1} text`} className="bg-brand-obsidian border-brand-line" />
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-lg border border-brand-line bg-brand-surface p-6" data-testid="content-why-section">
        <p className="font-eyebrow text-[10px] uppercase tracking-[0.25em] text-brand-crimson font-bold mb-5">Why Dogs Section</p>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-white/70 text-xs">Heading</Label>
            <Input data-testid="content-why-title" value={content.why.title || ""} onChange={(e) => setField("why", "title", e.target.value)} className="bg-brand-obsidian border-brand-line" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-white/70 text-xs">Intro Text</Label>
            <Textarea data-testid="content-why-intro" value={content.why.intro || ""} onChange={(e) => setField("why", "intro", e.target.value)} rows={3} className="bg-brand-obsidian border-brand-line" />
          </div>
          {(content.why.rows || []).map((row, i) => (
            <div key={i} className="grid sm:grid-cols-[230px_1fr] gap-3" data-testid={`content-why-row-${i}`}>
              <Input value={row.title || ""} onChange={(e) => setListField("why", "rows", i, "title", e.target.value)} placeholder={`Point ${i + 1} title`} className="bg-brand-obsidian border-brand-line" />
              <Input value={row.desc || ""} onChange={(e) => setListField("why", "rows", i, "desc", e.target.value)} placeholder={`Point ${i + 1} text`} className="bg-brand-obsidian border-brand-line" />
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-lg border border-brand-line bg-brand-surface p-6" data-testid="content-process-section">
        <p className="font-eyebrow text-[10px] uppercase tracking-[0.25em] text-brand-crimson font-bold mb-5">Deployment Boxes</p>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-white/70 text-xs">Section Heading</Label>
            <Input data-testid="content-process-title" value={content.process.title || ""} onChange={(e) => setField("process", "title", e.target.value)} className="bg-brand-obsidian border-brand-line" />
          </div>
          {(content.process.steps || []).map((step, i) => (
            <div key={i} className="grid sm:grid-cols-[230px_1fr] gap-3" data-testid={`content-process-step-${i}`}>
              <Input value={step.title || ""} onChange={(e) => setListField("process", "steps", i, "title", e.target.value)} placeholder={`Box ${i + 1} title`} className="bg-brand-obsidian border-brand-line" />
              <Input value={step.desc || ""} onChange={(e) => setListField("process", "steps", i, "desc", e.target.value)} placeholder={`Box ${i + 1} text`} className="bg-brand-obsidian border-brand-line" />
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-lg border border-brand-line bg-brand-surface p-6" data-testid="content-band-section">
        <p className="font-eyebrow text-[10px] uppercase tracking-[0.25em] text-brand-crimson font-bold mb-5">Quote Band ("One dog, one sweep...")</p>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-white/70 text-xs">Quote Text</Label>
            <Input data-testid="content-band-quote" value={content.band.quote || ""} onChange={(e) => setField("band", "quote", e.target.value)} className="bg-brand-obsidian border-brand-line" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-white/70 text-xs">Band Image</Label>
            <ImageUpload value={content.band.image} onChange={(url) => setField("band", "image", url)} testid="content-band-image-upload" />
          </div>
        </div>
      </section>

      <section className="rounded-lg border border-brand-line bg-brand-surface p-6" data-testid="content-cta-section">
        <p className="font-eyebrow text-[10px] uppercase tracking-[0.25em] text-brand-crimson font-bold mb-5">Bottom Section ("Secure your venue...")</p>
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-white/70 text-xs">Eyebrow Label</Label>
              <Input data-testid="content-cta-eyebrow" value={content.cta.eyebrow || ""} onChange={(e) => setField("cta", "eyebrow", e.target.value)} className="bg-brand-obsidian border-brand-line" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-white/70 text-xs">Heading</Label>
              <Input data-testid="content-cta-title" value={content.cta.title || ""} onChange={(e) => setField("cta", "title", e.target.value)} className="bg-brand-obsidian border-brand-line" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-white/70 text-xs">Text</Label>
            <Textarea data-testid="content-cta-text" value={content.cta.text || ""} onChange={(e) => setField("cta", "text", e.target.value)} rows={3} className="bg-brand-obsidian border-brand-line" />
          </div>
        </div>
      </section>
      </>
      )}

      <Button onClick={save} disabled={busy} data-testid="content-save-button" className="w-full bg-brand-crimson hover:bg-brand-crimhover text-white font-eyebrow uppercase tracking-[0.2em] text-xs font-bold py-6">
        {busy ? "Saving..." : "Save Site Content"} <Save className="h-4 w-4" />
      </Button>
    </div>
  );
}
